import React, {
	useCallback,
	useMemo,
	useRef,
	type ComponentType,
	type PropsWithChildren,
} from 'react';
import { loadEntryPoint } from 'react-relay';
import { Box } from '@atlaskit/primitives/compiled/box';
import { useEntryPointPreload } from '@atlassian/entry-point-use-trigger/preload';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import type {
	UniversalCreateButtonProps,
	UniversalCreateMenuSection,
} from '@atlassian/universal-create/types';
import {
	AiPlannerIframeProvider,
	useAiPlannerIframe,
} from '@jira/ai-planning__ai-planner-iframe-modal/src/AiPlannerIframeProvider.tsx';
import { useAiPlannerUniversalCreateEnabled } from '@jira/ai-planning__ai-plans-overview/src/useAiPlannerUniversalCreateEnabled.tsx';
import { SUPPORTED_MODERNISED_PROJECT_TYPES } from '@jira/issue-create__issue-create-common-types/src/common/constants/index.tsx';
import { useSmartFieldsMachineLearning } from '@jira/issue-create__issue-create-modal/src/common/utils/use-smart-fields-ml/index.tsx';
import type { IssueCreateModalLoadQuery$variables } from '@jira/issue-create__issue-create-modal/src/relay/modal-load/__generated__/IssueCreateModalLoadQuery.graphql.ts';
import { preloadModalLoadQuery } from '@jira/issue-create__issue-create-modal/src/services/modal-load-query/preloadModalLoadQuery.tsx';
import { getIssueCreateProjectForMount } from '@jira/issue-create__issue-create-modal/src/ui/modal/create-form/getIssueCreateProjectForMount.tsx';
import { getQueryVariables } from '@jira/issue-create__issue-create-modal/src/ui/modal/create-form/getQueryVariables.tsx';
import { useStorageVariables } from '@jira/issue-create__issue-create-modal/src/ui/modal/utils.tsx';
import { preloadIssueCreateModernisedModal } from '@jira/issue-create__issue-create-modernised/src/ui/modal/AsyncIssueCreateModal.tsx';
import { globalIssueCreateModernisedModalEntryPoint } from '@jira/issue-create__issue-create-modernised/src/ui/modal/globalIssueCreateModal.entrypoint.tsx';
import {
	useTriggerIssueCreateModal,
	initialState,
	noopActions,
} from '@jira/issue-create__use-trigger-issue-create-modal/src/main.tsx';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__topnav-common/src/TopLevelErrorBoundary.tsx';
import { fg } from '@jira/platform__feature-gating';
import { useIntl, defineMessage } from '@jira/platform__react-intl';
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx'; // eslint-disable-line jira/restricted/@jira+platform__services-providers-project-context
import { SpotlightTarget } from '@jira/platform__spotlight/src/ui/SpotlightTarget.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { NavigationCreateIssueButtonWrapperNudgeAsync } from '@jira/software__onboarding-nudges/src/NavigationCreateIssueButtonWrapperNudgeAsync.tsx';
import { getPerformance } from '@jira/ssr__browser-globals/src/getPerformance.tsx';
import { GLOBAL_CREATE_BUTTON_NAV4 } from './globalCreateButtonNav4.constants.tsx';
import { GLOBAL_CREATE_BUTTON_WRAPPER } from './globalCreateButtonWrapper.constants.tsx';
import { UNIVERSAL_CREATE_BUTTON_ID } from './universalCreateButtonId.constants.tsx';
import { useCreateIssueItem } from './useCreateIssueItem.tsx';
import { useCreateWithPlannerItem } from './useCreateWithPlannerItem.tsx';
import { useShouldShowDefaultCreateButton } from './useShouldShowDefaultCreateButton.tsx';
import { useUniversalCreateAnalytics } from './useUniversalCreateAnalytics.tsx';
import { useUniversalCreateButtonInteractiveExperienceComplete } from './useUniversalCreateButtonInteractiveExperienceComplete.tsx';
import { useUniversalCreateButtonInteractiveExperienceStart } from './useUniversalCreateButtonInteractiveExperienceStart.tsx';
import { useUniversalCreateLoadExperience } from './useUniversalCreateLoadExperience.tsx';

const create = defineMessage({
	defaultMessage: 'Create',
	id: 'atlassian-navigation.universal-create.create',
	description: 'Create text for the universal create button',
});

const noopPreload = (): void => {
	// No-op: hover preload is disabled when the on-hover gate is off.
};

const gateOffIssueCreateState = [initialState, noopActions] as const;

const shouldPreloadModernisedGlobalCreate = (
	projectContext: ReturnType<typeof useProjectContext>['data'],
	shouldUseLegacyCreateExperience: boolean,
): boolean =>
	!shouldUseLegacyCreateExperience &&
	(!projectContext?.projectType ||
		SUPPORTED_MODERNISED_PROJECT_TYPES.includes(projectContext.projectType));

const useGicHoverApiPreload = functionWithCondition(
	() => fg('nike_united_gic_on_hover_') && !fg('relay-migration-gic-phase-1'),
	(): (() => void) => {
		const { data: projectContext } = useProjectContext();
		const storageVariables = useStorageVariables();
		const { withMachineLearning } = useSmartFieldsMachineLearning();
		return useCallback(() => {
			preloadModalLoadQuery({ projectContext, storageVariables, withMachineLearning });
		}, [projectContext, storageVariables, withMachineLearning]);
	},
	(): (() => void) => noopPreload,
);

const useUniversalCreateIssueCreateState = functionWithCondition(
	() => fg('relay-migration-gic-phase-1'),
	useTriggerIssueCreateModal,
	() => gateOffIssueCreateState,
);

const useGlobalIssueCreateModernisedModalEntryPointPreload = functionWithCondition(
	() => fg('relay-migration-gic-phase-1'),
	(): (() => void) => {
		const relayEnvironmentProvider = useRelayEnvironmentProvider();
		const { data: projectContext } = useProjectContext();
		const storageVariables = useStorageVariables();
		const loadGlobalIssueCreateModernisedModalEntryPoint = useCallback(() => {
			const { project, issueType, requestType, defaultValues } = getQueryVariables(
				getIssueCreateProjectForMount(projectContext),
				null,
			);
			const variables: IssueCreateModalLoadQuery$variables = {
				cloudId: storageVariables.cloudId,
				project,
				issueType,
				requestType,
				defaultValues,
				evaluateMiniMode: true,
			};

			return loadEntryPoint(
				relayEnvironmentProvider,
				globalIssueCreateModernisedModalEntryPoint,
				variables,
			);
		}, [projectContext, relayEnvironmentProvider, storageVariables.cloudId]);
		const { preloadReference } = useEntryPointPreload(
			loadGlobalIssueCreateModernisedModalEntryPoint,
		);

		return preloadReference;
	},
	(): (() => void) => noopPreload,
);

type UniversalCreateComponent = ComponentType<UniversalCreateButtonProps>;

export interface UniversalCreateProps {
	CreateComponent: UniversalCreateComponent;
}

function UniversalCreateInner({ CreateComponent }: UniversalCreateProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const triggerAnalytics = useUniversalCreateAnalytics();
	const aiPlannerEnabled = useAiPlannerUniversalCreateEnabled();
	// oxlint-disable-next-line react-hooks/rules-of-hooks
	const sharedPlannerIframe = aiPlannerEnabled ? useAiPlannerIframe() : undefined;
	const warmPlanner = sharedPlannerIframe?.warm;

	useUniversalCreateLoadExperience();
	useUniversalCreateButtonInteractiveExperienceStart();

	const createIssueItem = useCreateIssueItem({});
	const createWithPlannerItem = aiPlannerEnabled
		? // oxlint-disable-next-line react-hooks/rules-of-hooks
			useCreateWithPlannerItem()
		: undefined;

	const plannerCreateEnabled = aiPlannerEnabled && createWithPlannerItem !== undefined;
	const shouldShowDefaultCreateButton = useShouldShowDefaultCreateButton();
	const { data: projectContext } = useProjectContext();
	const [{ shouldUseLegacyCreateExperience }] = useUniversalCreateIssueCreateState();
	const preloadGlobalIssueCreateModernisedModalEntryPoint =
		useGlobalIssueCreateModernisedModalEntryPointPreload();

	const menuSection = useMemo(() => {
		if (plannerCreateEnabled && createWithPlannerItem) {
			return {
				items: [
					createWithPlannerItem,
					...(!fg('jira_uc_split_button_enabled') ? [createIssueItem] : []),
				],
			};
		}
		return {
			items: [createIssueItem],
		};
	}, [plannerCreateEnabled, createIssueItem, createWithPlannerItem]);

	const sections = useMemo(
		(): UniversalCreateMenuSection[] => [
			{
				type: 'appDefinedSection',
				items: menuSection.items,
			},
		],
		[menuSection.items],
	);

	// Primary button needs its own onClick that fires analytics with isPrimary: true
	const primaryActionItem = useMemo(
		() => ({
			...createIssueItem,
			onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
				triggerAnalytics({ isPrimary: true });
				createIssueItem.onClick?.(event);
			},
		}),
		[createIssueItem, triggerAnalytics],
	);

	// Mark interactive experience as complete (items are loaded)
	useUniversalCreateButtonInteractiveExperienceComplete();

	const onDropdownMenuOpen = useCallback(() => {
		if (aiPlannerEnabled) {
			warmPlanner?.();
		}
		triggerAnalytics();
	}, [aiPlannerEnabled, triggerAnalytics, warmPlanner]);

	const onDefaultCreateClick = useCallback(() => {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		createIssueItem?.onClick?.({ timeStamp: getPerformance()?.now() ?? 0 } as React.MouseEvent);
	}, [createIssueItem]);

	const preloadGicApiOnHover = useGicHoverApiPreload();

	const hasPreloadedModernisedModalRef = useRef(false);
	const hasPreloadedGicApiRef = useRef(false);
	const handlePreloadTrigger = useCallback(() => {
		if (
			!hasPreloadedGicApiRef.current &&
			!fg('relay-migration-gic-phase-1') &&
			fg('nike_united_gic_on_hover_')
		) {
			hasPreloadedGicApiRef.current = true;
			preloadGicApiOnHover();
		}

		if (
			!shouldPreloadModernisedGlobalCreate(
				projectContext,
				shouldUseLegacyCreateExperience ?? false,
			) &&
			fg('relay-migration-gic-phase-1')
		) {
			return;
		}

		if (hasPreloadedModernisedModalRef.current) {
			return;
		}

		hasPreloadedModernisedModalRef.current = true;
		if (fg('relay-migration-gic-phase-1')) {
			preloadGlobalIssueCreateModernisedModalEntryPoint();
			return;
		}

		preloadIssueCreateModernisedModal();
	}, [
		preloadGicApiOnHover,
		preloadGlobalIssueCreateModernisedModalEntryPoint,
		projectContext,
		shouldUseLegacyCreateExperience,
	]);

	const conditionalProps = () => {
		if (shouldShowDefaultCreateButton) {
			return { buttonOnly: true as const, onClick: onDefaultCreateClick };
		}

		const menuSections = {
			sections,
		};

		// UC split button variant
		if (fg('jira_uc_split_button_enabled')) {
			return { primaryActionItem, onClick: onDropdownMenuOpen, ...menuSections };
		}

		// UC single button variant
		return { onClick: onDropdownMenuOpen, ...menuSections };
	};
	return (
		<Box
			testId={`${GLOBAL_CREATE_BUTTON_WRAPPER}`}
			onMouseEnter={handlePreloadTrigger}
			onFocus={handlePreloadTrigger}
		>
			<NavigationCreateIssueButtonWrapperNudgeAsync>
				<CreateButtonSpotlightTarget>
					<CreateComponent
						product="jira"
						testId={GLOBAL_CREATE_BUTTON_NAV4}
						{...conditionalProps()}
					>
						{formatMessage(create)}
					</CreateComponent>
				</CreateButtonSpotlightTarget>
			</NavigationCreateIssueButtonWrapperNudgeAsync>
		</Box>
	);
}

const CreateButtonSpotlightTarget = ({ children }: PropsWithChildren) => {
	return <SpotlightTarget name="global-create-spotlight">{children}</SpotlightTarget>;
};

const UniversalCreateWithPlannerProviders = ({
	CreateComponent,
}: UniversalCreateProps): React.JSX.Element => {
	const cloudId = useCloudId();
	const accountId = useAccountId() || '';
	const embedContext = useMemo(() => ({ cloudId, accountId }), [cloudId, accountId]);

	return (
		<AiPlannerIframeProvider embedContext={embedContext}>
			<ModalContextProvider>
				<UniversalCreateInner CreateComponent={CreateComponent} />
			</ModalContextProvider>
		</AiPlannerIframeProvider>
	);
};

export default function UniversalCreate({
	CreateComponent,
}: UniversalCreateProps): React.JSX.Element {
	return (
		<TopLevelErrorBoundary id={UNIVERSAL_CREATE_BUTTON_ID}>
			{fg('jira_planner_uc_enabled') ? (
				<UniversalCreateWithPlannerProviders CreateComponent={CreateComponent} />
			) : (
				<UniversalCreateInner CreateComponent={CreateComponent} />
			)}
		</TopLevelErrorBoundary>
	);
}
